import { createContext, useContext, useState, type ReactNode, useCallback } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import AddLeadModal from "@/pages/leads/components/AddLeadModal";
import LogCallModal from "@/pages/calls/components/LogCallModal";
import ScheduleCallModal from "@/pages/calls/components/ScheduleCallModal";
import QuickNoteModal from "@/components/feature/QuickNoteModal";
import type { Lead } from "@/mocks/leadsData";

interface GlobalModalContextValue {
  openAddLead: () => void;
  openLogCall: () => void;
  openScheduleCall: () => void;
  openQuickNote: () => void;
}

const GlobalModalContext = createContext<GlobalModalContextValue | null>(null);

export function useGlobalModal(): GlobalModalContextValue {
  const ctx = useContext(GlobalModalContext);
  if (!ctx) throw new Error("useGlobalModal must be used within GlobalModalProvider");
  return ctx;
}

export function GlobalModalProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [scheduleCallOpen, setScheduleCallOpen] = useState(false);
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);

  const openAddLead = useCallback(() => setAddLeadOpen(true), []);
  const openLogCall = useCallback(() => setLogCallOpen(true), []);
  const openScheduleCall = useCallback(() => setScheduleCallOpen(true), []);
  const openQuickNote = useCallback(() => setQuickNoteOpen(true), []);

  const handleAddLeadSave = useCallback(async (data: Omit<Lead, "id" | "lastActivity">, id?: number) => {
    try {
      if (id) {
        await fetchApi(`/leads/${id}`, { method: "PATCH", body: JSON.stringify(data) });
      } else {
        await fetchApi("/leads", { method: "POST", body: JSON.stringify(data) });
      }
      setAddLeadOpen(false);
      showToast(id ? "Lead updated successfully" : "Lead created successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to save lead", "error");
    }
  }, [showToast]);

  const handleLogCallSave = useCallback(async (data: any) => {
    try {
      await fetchApi("/calls", {
        method: "POST",
        body: JSON.stringify({
          contact_name: data.leadName,
          phone: data.phone,
          direction: "outbound",
          duration: data.duration,
          status: data.status || "Completed",
          outcome: data.outcome || "Connected",
          notes: data.notes,
          lead_id: data.leadId || null,
          agent: data.agent,
        }),
      });
      setLogCallOpen(false);
      showToast("Call logged successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to log call", "error");
    }
  }, [showToast]);

  const handleScheduleCallSave = useCallback(async (data: any) => {
    try {
      await fetchApi("/activities", {
        method: "POST",
        body: JSON.stringify({
          type: "call",
          title: "Call scheduled",
          description: `Follow-up call with ${data.leadName} scheduled for ${data.time}`,
          user_name: data.agent || "Agent",
          lead_name: data.leadName,
          detail: `${data.time} • ${data.purpose || "Follow-up"}`,
        }),
      });
      setScheduleCallOpen(false);
      showToast("Call scheduled successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to schedule call", "error");
    }
  }, [showToast]);

  return (
    <GlobalModalContext.Provider value={{ openAddLead, openLogCall, openScheduleCall, openQuickNote }}>
      {children}

      {/* Global modals rendered at root level */}
      {addLeadOpen && (
        <AddLeadModal
          lead={null}
          onClose={() => setAddLeadOpen(false)}
          onSave={handleAddLeadSave}
        />
      )}
      {logCallOpen && (
        <LogCallModal
          onClose={() => setLogCallOpen(false)}
          onSave={handleLogCallSave}
        />
      )}
      {scheduleCallOpen && (
        <ScheduleCallModal
          onClose={() => setScheduleCallOpen(false)}
          onSave={handleScheduleCallSave}
        />
      )}
      {quickNoteOpen && (
        <QuickNoteModal onClose={() => setQuickNoteOpen(false)} />
      )}
    </GlobalModalContext.Provider>
  );
}